import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export interface ChannelPillItem {
  id: string;
  name: string;
  isMaster?: boolean;
  badgeCount?: number;
  badgeLabel?: string;
}

export interface ChannelPillsSelectorProps {
  channels: ChannelPillItem[];
  activeChannelId: string | null;
  onChange: (channelId: string) => void;
  onAddChannel?: () => void;
  className?: string;
}

export function ChannelPillsSelector({
  channels,
  activeChannelId,
  onChange,
  onAddChannel,
  className,
}: ChannelPillsSelectorProps) {
  const { t } = useTranslation();

  if (channels.length === 0 && !onAddChannel) {
    return (
      <div className={cn('text-sm text-neutral-500 dark:text-neutral-400', className)}>
        {t('productContent.channels.pills.empty')}
      </div>
    );
  }

  return (
    <div
      role="tablist"
      aria-label={t('productContent.channels.pills.label')}
      className={cn('flex flex-wrap gap-2', className)}
    >
      {channels.map((channel) => {
        const isActive = channel.id === activeChannelId;
        return (
          <button
            key={channel.id}
            type="button"
            role="tab"
            aria-pressed={isActive}
            onClick={() => onChange(channel.id)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-primary-500',
              isActive
                ? 'bg-primary-500 text-white font-semibold'
                : 'bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
            )}
          >
            {channel.isMaster && <span aria-hidden="true">🏠</span>}
            <span>{channel.name}</span>
            {channel.badgeCount !== undefined && (
              <span
                className={cn(
                  'inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-medium',
                  isActive
                    ? 'bg-white text-primary-600'
                    : 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                )}
              >
                {channel.badgeCount}
              </span>
            )}
            {channel.badgeLabel && (
              <span
                className={cn(
                  'text-xs px-1.5 py-0.5 rounded',
                  isActive ? 'bg-white text-primary-600' : 'bg-emerald-100 text-emerald-700'
                )}
              >
                {channel.badgeLabel}
              </span>
            )}
          </button>
        );
      })}

      {onAddChannel && (
        <button
          type="button"
          onClick={onAddChannel}
          aria-label={t('productContent.channels.pills.addChannel')}
          className={cn(
            'inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm',
            'bg-white dark:bg-neutral-900 border border-dashed border-neutral-400 dark:border-neutral-600',
            'text-neutral-500 dark:text-neutral-400',
            'hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-primary-500'
          )}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          <span>{t('productContent.channels.pills.more')}</span>
        </button>
      )}
    </div>
  );
}
